// BeyPongPlayerLockCard — Iter 40. Player-facing lock panel for BEY-PONG.
// Renders a card on the tournament page when the logged-in user is one
// of the two players in a live BEY-PONG match whose phase is
// select_beys / lock_order / set_end. Lets them pick 3 of their 4
// registered beys and lock their hidden order without touching the
// referee panel. The opponent's order stays hidden until both lock.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Lock, LockOpen, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { t } from "../../lib/i18n";

export default function BeyPongPlayerLockCard({ tournament, matches, players, myPid, onLockedChange }) {
  const [componentsById, setComponentsById] = useState({});
  const [myDeck, setMyDeck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [orderDraft, setOrderDraft] = useState([]);
  const [pickDraft, setPickDraft] = useState([]);

  // The active (non-completed) BEY-PONG match this player is part of.
  const myMatch = useMemo(() => {
    if (!myPid) return null;
    return (matches || []).find(
      (m) =>
        m.status !== "completed" &&
        (m.player_a_id === myPid || m.player_b_id === myPid),
    );
  }, [matches, myPid]);

  useEffect(() => {
    let cancelled = false;
    api.get("/components").then((r) => {
      if (cancelled) return;
      const map = {};
      r.data.forEach((c) => (map[c.id] = c));
      setComponentsById(map);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!myPid || !tournament?.id) return;
    let cancelled = false;
    api.get(`/tournaments/${tournament.id}/registrations`)
      .then(async (r) => {
        if (cancelled) return;
        const mine = (r.data || []).find((x) => x.player_id === myPid);
        if (!mine?.deck_id) { setMyDeck(null); return; }
        const d = await api.get(`/decks/${mine.deck_id}`).then((rr) => rr.data).catch(() => null);
        if (!cancelled) setMyDeck(d);
      })
      .catch(() => setMyDeck(null));
    return () => { cancelled = true; };
  }, [myPid, tournament?.id]);

  const myBeys = useMemo(() => (myDeck?.beys || []).map((b, idx) => {
    const pid = b.blade || b.over_blade;
    const c = pid ? componentsById[pid] : null;
    return { id: pid || `bey-${idx}`, label: c?.name || (pid ? pid.slice(0, 6) : `Bey ${idx + 1}`) };
  }), [myDeck, componentsById]);

  // Only rendered when we're in a BEY-PONG tournament and the user has
  // a live match. Everything else returns null.
  if (tournament?.scoring_format !== "bey_pong") return null;
  if (!myMatch) return null;
  const state = myMatch.bey_pong_state;
  const side = myMatch.player_a_id === myPid ? "a" : "b";
  const iSelected = state?.[`selected_${side}`] || [];
  const iLocked = !!state?.[`order_${side}_locked`];
  const opponentLocked = !!state?.[side === "a" ? "order_b_locked" : "order_a_locked"];
  const phase = state?.phase;
  const revealed = !!state?.order_revealed;

  const commitSelection = async () => {
    if (pickDraft.length !== 3) {
      toast.error(t.beyPongSelectThree);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/matches/${myMatch.id}/beypong/select-three`, {
        player_id: myPid,
        bey_ids: pickDraft,
      });
      toast.success(t.saved);
      onLockedChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failed);
    } finally {
      setBusy(false);
    }
  };

  const commitOrder = async () => {
    if (orderDraft.length !== 3) return;
    setBusy(true);
    try {
      await api.post(`/matches/${myMatch.id}/beypong/lock-order`, {
        player_id: myPid,
        order: orderDraft,
      });
      toast.success(t.beyPongOrderLocked);
      onLockedChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.failed);
    } finally {
      setBusy(false);
    }
  };

  // Card body renderer branches on phase
  return (
    <div
      data-testid="beypong-player-lock-card"
      className="border border-[#E2FF00]/40 bg-[#0C0C14] p-4 mt-4 space-y-3"
    >
      <div className="text-[10px] uppercase tracking-[0.3em] font-mono font-bold text-[#E2FF00]">
        BEY-PONG · {t.beyPongCurrentSet} {(state?.set_index ?? 0) + 1}
      </div>

      {myBeys.length < 4 && (
        <div
          data-testid="beypong-player-no-deck"
          className="text-[11px] font-mono text-[#FF0055]"
        >
          {t.beyPongNoDeck || "You have not registered a 4-Bey BEY-PONG deck yet. Join the tournament with a BEY-PONG deck (4 Beys) to lock in."}
        </div>
      )}

      {myBeys.length >= 4 && phase === "select_beys" && iSelected.length === 0 && (
        <div className="space-y-2" data-testid="beypong-player-pickthree">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60">
            {t.beyPongSelectThree}
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {myBeys.map((o) => {
              const on = pickDraft.includes(o.id);
              return (
                <button
                  key={o.id}
                  onClick={() =>
                    setPickDraft((p) =>
                      on ? p.filter((x) => x !== o.id) : p.length < 3 ? [...p, o.id] : p,
                    )
                  }
                  disabled={busy}
                  data-testid={`beypong-player-pick-${o.id}`}
                  className={`text-left px-2 py-1.5 text-[11px] border font-mono ${on ? "border-[#E2FF00] bg-[#E2FF00]/10 text-[#E2FF00]" : "border-white/15 text-white/70 hover:border-white/30"}`}
                >
                  {on ? "✓ " : ""}{o.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={commitSelection}
            disabled={busy || pickDraft.length !== 3}
            data-testid="beypong-player-commit-pick"
            className="px-3 py-1.5 bg-[#FF0055] text-white text-[10px] uppercase font-bold tracking-[0.25em] disabled:opacity-40"
          >
            {t.beyPongSelectThree}
          </button>
        </div>
      )}

      {myBeys.length >= 4 && (phase === "lock_order" || phase === "set_end") && !iLocked && iSelected.length === 3 && (
        <div className="space-y-2" data-testid="beypong-player-orderbuild">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60 flex items-center gap-1.5">
            <LockOpen size={11} /> {t.beyPongHiddenOrder} — 1 → 2 → 3
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {iSelected.map((id) => {
              const label = myBeys.find((b) => b.id === id)?.label || id.slice(0, 6);
              const idx = orderDraft.indexOf(id);
              const picked = idx >= 0;
              return (
                <button
                  key={id}
                  disabled={busy || picked}
                  onClick={() => setOrderDraft((p) => (p.includes(id) ? p : p.length < 3 ? [...p, id] : p))}
                  data-testid={`beypong-player-order-${id}`}
                  className={`text-left px-2 py-1.5 text-[11px] border font-mono ${picked ? "border-[#E2FF00] bg-[#E2FF00]/10 text-[#E2FF00]" : "border-white/15 text-white/70 hover:border-white/30"}`}
                >
                  {picked ? `${idx + 1}. ` : ""}{label}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setOrderDraft([])}
              disabled={busy || orderDraft.length === 0}
              className="px-2 py-1 border border-white/20 text-white/60 text-[10px] uppercase disabled:opacity-30"
              data-testid="beypong-player-order-reset"
            >
              Reset
            </button>
            <button
              onClick={commitOrder}
              disabled={busy || orderDraft.length !== 3}
              data-testid="beypong-player-lock-btn"
              className="px-3 py-1.5 bg-[#FF0055] text-white text-[10px] uppercase font-bold tracking-[0.25em] disabled:opacity-40 inline-flex items-center gap-1.5"
            >
              <Lock size={11} /> {t.beyPongLockOrder}
            </button>
          </div>
        </div>
      )}

      {iLocked && !revealed && (
        <div
          data-testid="beypong-player-waiting"
          className="text-[11px] font-mono text-[#E2FF00] flex items-center gap-2"
        >
          <CheckCircle size={13} weight="fill" /> {t.beyPongOrderLocked} · {t.beyPongWaitingOpponent}
        </div>
      )}

      {revealed && (
        <div
          data-testid="beypong-player-live"
          className="text-[11px] font-mono text-white/60"
        >
          {t.beyPongOrderLocked} · {t.beyPongCurrentRound} {(state?.round_in_cycle ?? 0) + 1}/3
        </div>
      )}

      <div className="text-[9px] font-mono text-white/30 pt-2 border-t border-white/10">
        {opponentLocked
          ? t.beyPongOrderLocked + " ✓"
          : t.beyPongWaitingOpponent}
      </div>
    </div>
  );
}
